import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  Menu,
  Modal,
  NumberFormatter,
  Progress,
} from "@mantine/core";
import {
  Appointment,
  AppointmentStatus,
} from "../../../interfaces/services/Appointment";
import { parseFixedISOStringToDate } from "../../../utils/dateUtils";
import { getStatusColor } from "../../../pages/profile/Appointment/AppointmentsCalendarPage";
import { BsThreeDotsVertical } from "react-icons/bs";
import { FaCheck, FaInfoCircle, FaTimes, FaUserTimes } from "react-icons/fa";
import { copyToClipboard } from "../../../utils/copyToClipboard";
import { Copy } from "lucide-react";
import { translateAppointmentStatusToTurkish } from "../../../utils/frontFix";

export default function AppointmentDetailsModal({
  opened,
  onClose,
  a,
  canAct,
  changeStatus,
}: {
  opened: boolean;
  onClose: () => void;
  a: Appointment;
  canAct: {
    canUpdate: boolean;
  };
  changeStatus: (a: Appointment, status: AppointmentStatus) => Promise<void>;
}) {
  if (!a) return null;

  const start = parseFixedISOStringToDate(
    a.appointmentTime.appointmentStartDate
  );
  const end = parseFixedISOStringToDate(a.appointmentTime.appointmentEndDate);

  // ---- Seans bilgisi (etiket + progress) ----
  const ss = a.serviceSnapshot;
  const isSession = ss?.type === "Session";
  const cur =
    typeof a.sessionNumber === "number" && a.sessionNumber >= 1
      ? a.sessionNumber
      : null;
  const total =
    typeof ss?.sessionCount === "number" && ss.sessionCount >= 1
      ? ss.sessionCount
      : null;
  const intervalDays =
    typeof ss?.sessionIntervalDays === "number" && ss.sessionIntervalDays >= 1
      ? ss.sessionIntervalDays
      : null;

  const sessionLine = isSession
    ? `${cur ?? "?"}/${total ?? "?"} seans${
        intervalDays ? ` • ${intervalDays}g` : ""
      }`
    : "";

  const progressValue =
    isSession && cur && total
      ? Math.min(100, Math.max(0, (cur / total) * 100))
      : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <p>Randevu Detayları</p>
          <Badge
            variant="light"
            style={{ background: getStatusColor(a.status), color: "white" }}
          >
            {translateAppointmentStatusToTurkish(a.status)}
          </Badge>
        </div>
      }
      centered
      size="lg"
      withinPortal
    >
      <div className="flex flex-col gap-4">
        {/* Üst bilgi satırı */}
        <div>
          <Group justify="space-between">
            <Group gap="xs">
              <span className="text-lg sm:text-xl">
                {start.toLocaleDateString("tr-TR")} •{" "}
                <span className="font-bold text-blue-500 text-2xl sm:text-3xl">
                  {start.toLocaleTimeString("tr-TR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {" – "}
                  {end.toLocaleTimeString("tr-TR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </span>
            </Group>

            {canAct.canUpdate && a.status === "Approved" && (
              <Menu shadow="md">
                <Menu.Target>
                  <ActionIcon variant="filled" aria-label="İşlemler">
                    <BsThreeDotsVertical />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<FaCheck />}
                    onClick={() => changeStatus(a, "Completed")}
                  >
                    Tamamlandı Olarak İşaretle
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<FaTimes />}
                    onClick={() => changeStatus(a, "Canceled")}
                  >
                    İptal Olarak İşaretle
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<FaUserTimes />}
                    onClick={() => changeStatus(a, "NoShow")}
                  >
                    Gelmedi İşaretle
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </div>

        <Divider />

        {/* Hizmet bilgileri */}
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium text-gray-500">Hizmet Bilgileri</p>

          <div className="flex items-center gap-2 text-3xl font-semibold">
            <NumberFormatter
              prefix="₺"
              value={a.serviceSnapshot?.price || 0}
              thousandSeparator
              className="text-blue-500 font-bold"
            />
            <Divider orientation="vertical" />
            <span className="text-base">
              {a.serviceSnapshot?.name || "Servis"}
            </span>
            <Divider orientation="vertical" />
            <span className="text-base">
              {a.serviceSnapshot?.duration || "0"} dk
            </span>
          </div>

          {/* Seans rozeti + etiketi + progress */}
          {isSession && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="filled" color="blue" radius="sm">
                  Seanslı
                </Badge>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {sessionLine}
                </span>
              </div>
              {progressValue !== null && (
                <Progress value={progressValue} size="sm" radius="sm" />
              )}
            </div>
          )}
        </div>

        <Divider />

        {/* Çalışan */}
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-gray-500">Çalışan</p>
          <p className="font-semibold">
            {a.employeeSnapshot?.name ?? "Personel"}{" "}
            {a.employeeSnapshot?.lastname}
          </p>
        </div>

        <Divider />

        {/* Müşteri */}
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-gray-500">Müşteri Bilgileri</p>
          {a.customerSnapshot ? (
            <div className="flex items-center gap-3">
              <Avatar
                size="lg"
                src={a.customerSnapshot.imageUrl}
                name={`${a.customerSnapshot.name} ${a.customerSnapshot.lastname}`}
                alt={a.customerSnapshot.name}
              />
              <div>
                <p className="font-semibold">
                  {a.customerSnapshot.name} {a.customerSnapshot.lastname}
                </p>
                {a.customerSnapshot.phone && (
                  <p
                    className="text-sm text-gray-600 flex items-center cursor-pointer gap-2 hover:text-blue-500"
                    onClick={() =>
                      copyToClipboard(a.customerSnapshot?.phone ?? "")
                    }
                  >
                    {a.customerSnapshot.phone}
                    <Copy size={14} className="inline-block" />
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-600">Müşteri profili yok.</p>
          )}
          {a.note && (
            <Alert
              variant="light"
              color="blue"
              title="Not"
              icon={<FaInfoCircle />}
            >
              {a.note}
            </Alert>
          )}
        </div>

        {/* Durum bilgi bandı */}
        {a.status === "Approved" && (
          <div className="flex flex-col gap-2">
            <Button
              leftSection={<FaCheck />}
              color="green"
              onClick={() => changeStatus(a, "Completed")}
            >
              Tamamlandı Olarak İşaretle
            </Button>
            <Button
              leftSection={<FaTimes />}
              color="red"
              onClick={() => changeStatus(a, "Canceled")}
            >
              İptal Olarak İşaretle
            </Button>
            <Button
              leftSection={<FaUserTimes />}
              color="gray"
              onClick={() => changeStatus(a, "NoShow")}
            >
              Gelmedi İşaretle
            </Button>
          </div>
        )}
        {a.status === "Completed" && (
          <Alert
            variant="light"
            color={getStatusColor(a.status)}
            title="Randevu Tamamlandı"
            icon={<FaInfoCircle />}
          />
        )}
        {a.status === "Canceled" && (
          <Alert
            variant="light"
            color={getStatusColor(a.status)}
            title="Randevu İptal Edildi"
            icon={<FaInfoCircle />}
          />
        )}
        {a.status === "NoShow" && (
          <Alert
            variant="light"
            color={getStatusColor(a.status)}
            title="Müşteri Randevuya Gelmedi"
            icon={<FaInfoCircle />}
          />
        )}
      </div>
    </Modal>
  );
}
